import React, { FC, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useClearRefinements } from 'react-instantsearch';
import { useTranslation } from '~/shared/utils/translation';
import IRefinementList from './RefinementList.definitions';
import { Button, SwatchColor } from '~/shared/components';
import Flex from '~/shared/components/Flex';
import rowStyles from '~/features/productList/components/RefinementListRow/RefinementListRow.module.scss';
import { RefinementSearchInput } from '~/features/productList/components/RefinementSearchInput';
import { FilterSelectionHeader } from '~/features/productList/components/FilterSelectionHeader';
import { StickyFilterHeader } from '~/features/productList/components/StickyFilterHeader';
import useColorSwatches from '~/features/productList/hooks/useColorSwatches';
import { useDebounce } from 'react-use';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import { usePinnedRefinementItems } from '~/features/productList/hooks/usePinnedRefinementItems';
import { VirtualizedList } from '~/features/productList/components/VirtualizedList';
import Checkmark from '~/icons/checkmark-alt.svg';
import RefinementEmptyList from '../../FilterList/components/RefinementEmptyList';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import { useScrollToTopOnSearch } from '~/features/productList/hooks/useScrollToTopOnSearch';

export const RefinementListColor: FC<IRefinementList> = ({
    attribute,
    label,
    searchable,
    showCount,
    isActive = true,
}) => {
    const { translate } = useTranslation();
    const { trackFilter } = useDrawerFilterTracking();
    const { colorSwatches } = useColorSwatches();
    const [searchValue, setSearchValue] = useState('');

    useScrollToTopOnSearch(searchValue);

    const {
        refine,
        searchForItems,
        allItems: fetchedItems,
    } = useOptimizedRefinementList({
        attribute,
        escapeFacetValues: false,
    });
    const items = usePinnedRefinementItems(fetchedItems, searchValue, isActive);
    const { refine: clearSelection } = useClearRefinements({ includedAttributes: [attribute] });
    const selectedCount = items.filter((item) => item.isRefined).length;

    const handleRefine = useCallback((value: string) => {
        if (!value) return;
        trackFilter(attribute, value, {
            isRefined: items.find((item) => item.value === value)?.isRefined,
        });

        refine(value);
    }, []);

    const handleSearch = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchValue(event.target.value);
        },
        [attribute, items],
    );

    useDebounce(
        () => {
            searchForItems(searchValue ?? '');
        },
        200,
        [searchValue],
    );

    useEffect(() => {
        if (searchValue) {
            searchForItems(searchValue ?? '');
        }
    }, [searchValue, items]);

    return (
        <>
            <StickyFilterHeader>
                {searchable && (
                    <RefinementSearchInput
                        value={searchValue}
                        onChange={handleSearch}
                        onClear={() => setSearchValue('')}
                        placeholder={translate('plp.searchPlaceholder')}
                    />
                )}

                <FilterSelectionHeader
                    label={label}
                    selectedCount={selectedCount}
                    onClear={clearSelection}
                />
            </StickyFilterHeader>

            {items.length === 0 ? (
                <RefinementEmptyList />
            ) : (
                <VirtualizedList
                    isActive={isActive}
                    items={items}
                    itemKey={(item) => `${item.label}-${item.value}`}
                    renderItem={({ value, label: itemLabel, count, isRefined }) => {
                        const imgSrc = colorSwatches?.find(
                            (swatch) => swatch.key === value,
                        )?.imageUrl;

                        return (
                            <Flex grow className={rowStyles.dropdownItemContainer}>
                                <Button
                                    className={rowStyles.dropdownItemButton}
                                    fullWidth
                                    variant="plain"
                                    onClick={() => handleRefine(value)}
                                    tabIndex={-1}
                                >
                                    <Flex
                                        grow
                                        alignItems="center"
                                        className={rowStyles.dropdownItemText}
                                    >
                                        <Flex className={rowStyles.dropdownTextWrapper}>
                                            <span
                                                className={clsx(rowStyles.checkmarkContainer, {
                                                    [rowStyles.isRefined]: isRefined,
                                                })}
                                                role="checkbox"
                                                aria-label={`${itemLabel}, ${count} produkter`}
                                                aria-checked={isRefined}
                                                tabIndex={0}
                                                onKeyDown={(event) => {
                                                    if (event.key !== ' ' && event.key !== 'Enter')
                                                        return;
                                                    event.preventDefault();
                                                    handleRefine(value);
                                                }}
                                            >
                                                {isRefined && (
                                                    <Checkmark className={rowStyles.checkmark} />
                                                )}
                                            </span>
                                            <SwatchColor src={imgSrc} alt={itemLabel} />
                                            {itemLabel}
                                        </Flex>
                                        <span className={rowStyles.dropdownValueWrapper}>
                                            {showCount && count ? count : ''}
                                        </span>
                                    </Flex>
                                </Button>
                            </Flex>
                        );
                    }}
                />
            )}
        </>
    );
};

export default RefinementListColor;
